import React, { useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { setDocumentTitle } from '../../utils/documentTitle';
import LanguageSwitcher from '../LanguageSwitcher';

const WelcomePage = () => {
  const { t } = useTranslation();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const hasRedirected = useRef(false);

  useEffect(() => {
    setDocumentTitle('');
  }, []);

  // ถ้าล็อกอินแล้วให้ redirect ไปหน้า nurse (สำหรับ NurseApp)
  // แต่ไม่ redirect ถ้าอยู่ใน marketplace routes แล้ว (เพื่อไม่ให้ interrupt navigation)
  // และไม่ redirect ถ้ากำลัง navigate จาก notification (มี query params หรือ hash)
  useEffect(() => {
    const currentPath = window.location.pathname;
    const hasQueryParams = window.location.search || window.location.hash;
    const isNavigatingFromNotification = hasQueryParams && currentPath.startsWith('/marketplace');
    
    if (currentUser && !hasRedirected.current && !currentPath.startsWith('/marketplace') && !isNavigatingFromNotification) {
      hasRedirected.current = true;
      navigate('/nurse', { replace: true });
    }
  }, [currentUser, navigate]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-white flex flex-col md:block">
      {/* Header - Fixed */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md shadow-sm">
        <div className="container mx-auto px-3 md:px-4 py-2 md:py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <div className="flex items-center space-x-2">
              <img 
                src="/logo.png" 
                alt="CannaTrade Logo" 
                className="w-8 h-8 md:w-10 md:h-10 object-contain"
                onError={(e) => {
                  // Fallback to emoji if image not found
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
              <div className="w-8 h-8 md:w-10 md:h-10 bg-gradient-to-br from-green-400 to-green-600 rounded-lg flex items-center justify-center hidden">
                <span className="text-xl md:text-2xl">🌿</span>
              </div>
              <h1 className="text-lg md:text-xl font-bold text-gray-800">CannaTrade</h1>
            </div>

            {/* Auth Buttons & Language Switcher */}
            <div className="flex items-center space-x-2 md:space-x-3">
              <LanguageSwitcher />
              <Link
                to="/login"
                className="px-2 md:px-4 py-1.5 md:py-2 text-xs md:text-sm font-medium text-gray-700 hover:text-gray-900 transition"
              >
                {t('common.login')}
              </Link>
              <Link
                to="/register"
                className="px-2 md:px-4 py-1.5 md:py-2 text-xs md:text-sm font-medium text-white bg-gradient-to-r from-green-500 to-green-600 rounded-lg hover:from-green-600 hover:to-green-700 transition shadow-md"
              >
                {t('common.register')}
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content - Mobile: Single View */}
      <div className="flex-1 flex flex-col pt-14 md:pt-20">
        {/* Hero Section - Compact on Mobile */}
        <section className="px-3 md:px-4 pt-2 md:pt-8 pb-3 md:pb-12 flex-shrink-0">
          <div className="container mx-auto">
            <div className="relative rounded-xl md:rounded-2xl overflow-hidden shadow-lg md:shadow-2xl">
              {/* Hero Image with Gradient Overlay */}
              <div className="relative h-[35vh] md:h-[60vh] min-h-[200px] md:min-h-[400px] bg-gradient-to-br from-green-400 via-green-500 to-green-600">
                {/* Background Pattern */}
                <div className="absolute inset-0 opacity-10">
                  <div className="absolute inset-0" style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                  }}></div>
                </div>

                {/* Content */}
                <div className="relative h-full flex flex-col items-center justify-center text-center px-3 md:px-4 text-white">
                  <div className="mb-2 md:mb-6">
                    {/* Logo with transparent background - Flip 3D with sparkle effect */}
                    <div className="relative inline-block">
                      <img 
                        src="/logo.png" 
                        alt="CannaTrade Logo" 
                        className="w-32 h-32 md:w-40 md:h-40 lg:w-48 lg:h-48 mx-auto mb-2 md:mb-4 object-contain logo-rotate-glow"
                        style={{ 
                          backfaceVisibility: 'hidden',
                          WebkitBackfaceVisibility: 'hidden'
                        }}
                        onError={(e) => {
                          // Fallback to emoji if image not found
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'block';
                        }}
                      />
                    </div>
                    <span className="text-4xl md:text-6xl mb-2 md:mb-4 block hidden">🌿</span>
                  </div>
                  <h2 className="text-2xl md:text-4xl lg:text-5xl font-bold mb-2 md:mb-4 drop-shadow-lg">
                    {t('welcome.title')}
                  </h2>
                  <p className="text-sm md:text-xl lg:text-2xl mb-2 md:mb-8 drop-shadow-md opacity-95">
                    {t('welcome.subtitle')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Content Section - Compact on Mobile */}
        <section className="px-3 md:px-4 py-2 md:py-12 flex-shrink-0">
          <div className="container mx-auto text-center">
            <h3 className="text-lg md:text-3xl lg:text-4xl font-bold text-gray-800 mb-1 md:mb-4">
              {t('welcome.platformFor')}
            </h3>
            <p className="text-xs md:text-lg lg:text-xl text-gray-600 mb-2 md:mb-8">
              {t('welcome.platformDescription')}
            </p>
          </div>
        </section>

        {/* CTA Section - Compact on Mobile */}
        <section className="px-3 md:px-4 py-2 md:py-8 flex-shrink-0">
          <div className="container mx-auto max-w-md">
            <div className="flex flex-col space-y-2 md:space-y-4">
              {/* Primary CTA */}
              <Link
                to="/register"
                className="w-full py-2.5 md:py-4 px-4 md:px-6 bg-gradient-to-r from-green-500 to-green-600 text-white text-sm md:text-lg font-semibold rounded-lg md:rounded-xl hover:from-green-600 hover:to-green-700 transition shadow-lg transform hover:scale-105 text-center"
              >
                {t('welcome.getStarted')}
              </Link>

              {/* Secondary CTA */}
              <Link
                to="/login"
                className="w-full py-2.5 md:py-4 px-4 md:px-6 bg-white border-2 border-green-500 text-green-600 text-sm md:text-lg font-semibold rounded-lg md:rounded-xl hover:bg-green-50 transition text-center"
              >
                {t('common.login')}
              </Link>
            </div>
          </div>
        </section>

        {/* Features Section - Compact Grid on Mobile */}
        <section className="px-3 md:px-4 py-3 md:py-12 bg-gray-50 flex-shrink-0">
          <div className="container mx-auto">
            <div className="grid grid-cols-3 md:grid-cols-3 gap-2 md:gap-6">
              {/* Feature 1 */}
              <div className="bg-white p-2 md:p-6 rounded-lg md:rounded-xl shadow-md text-center hover:shadow-lg transition">
                <div className="text-2xl md:text-4xl mb-1 md:mb-4">🌿</div>
                <h4 className="text-xs md:text-xl font-semibold text-gray-800 mb-1 md:mb-2">{t('welcome.products')}</h4>
                <p className="text-gray-600 text-[10px] md:text-sm hidden md:block">
                  {t('welcome.productsDescription')}
                </p>
              </div>

              {/* Feature 2 */}
              <div className="bg-white p-2 md:p-6 rounded-lg md:rounded-xl shadow-md text-center hover:shadow-lg transition">
                <div className="text-2xl md:text-4xl mb-1 md:mb-4">🏪</div>
                <h4 className="text-xs md:text-xl font-semibold text-gray-800 mb-1 md:mb-2">{t('welcome.stores')}</h4>
                <p className="text-gray-600 text-[10px] md:text-sm hidden md:block">
                  {t('welcome.storesDescription')}
                </p>
              </div>

              {/* Feature 3 */}
              <div className="bg-white p-2 md:p-6 rounded-lg md:rounded-xl shadow-md text-center hover:shadow-lg transition">
                <div className="text-2xl md:text-4xl mb-1 md:mb-4">⭐</div>
                <h4 className="text-xs md:text-xl font-semibold text-gray-800 mb-1 md:mb-2">{t('welcome.reviews')}</h4>
                <p className="text-gray-600 text-[10px] md:text-sm hidden md:block">
                  {t('welcome.reviewsDescription')}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Footer - Compact on Mobile */}
        <footer className="px-3 md:px-4 py-3 md:py-8 border-t border-gray-200 flex-shrink-0 mt-auto">
          <div className="container mx-auto">
            <div className="flex flex-col md:flex-row justify-center items-center space-y-1 md:space-y-0 md:space-x-6 text-[10px] md:text-sm text-gray-600">
              <Link to="/terms" className="hover:text-gray-900 transition">
                {t('welcome.termsOfService')}
              </Link>
              <span className="hidden md:inline">|</span>
              <Link to="/privacy" className="hover:text-gray-900 transition">
                {t('welcome.privacyPolicy')}
              </Link>
              <span className="hidden md:inline">|</span>
              <Link to="/contact" className="hover:text-gray-900 transition">
                {t('welcome.contactUs')}
              </Link>
            </div>
            <div className="text-center mt-2 md:mt-4 text-[9px] md:text-xs text-gray-500">
              {t('welcome.copyright')}
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default WelcomePage;

